import { Inject, Injectable, NgZone } from '@angular/core';

import { Capacitor } from '@capacitor/core';
import { concatMap, filter, from } from 'rxjs';
import { getToken } from 'firebase/messaging';
import { v4 } from 'uuid';
import { Router } from '@angular/router';

import { FcmTokenTypes } from '@shared/user/enums';
import { APP_VARIABLES_TOKEN, AppVariables } from '@tablesready/frontend-variables';
import { AuthService } from '@tablesready/frontend-auth';
import { LocalStorageService } from '@tablesready/frontend-storage';
import { NotificationCenterService } from '@tablesready/frontend-notification-center';
import { LocationSwitcherService } from '@tablesready/frontend-location-switcher';
import { LoggerService } from '@tablesready/frontend-logger';
import { FcmTokenApiService } from './fcm-token-api.service';
import { FirebaseService } from './firebase.service';

@Injectable({ providedIn: 'root' })
export class PushNotificationsService {
  constructor(
    @Inject(APP_VARIABLES_TOKEN) private readonly appVariables: AppVariables,
    private readonly authService: AuthService,
    private readonly storage: LocalStorageService,
    private readonly fcmTokenApiService: FcmTokenApiService,
    private readonly firebaseService: FirebaseService,
    private readonly router: Router,
    private readonly notificationCenterService: NotificationCenterService,
    private readonly ngZone: NgZone,
    private readonly locationSwitcherService: LocationSwitcherService,
    private readonly logger: LoggerService,
  ) {}

  async init(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      return;
    }

    if (!('serviceWorker' in navigator)) {
      return;
    }

    // Remove the old Firebase SW registered at /firebase-cloud-messaging-push-scope.
    // That SW intercepts push events and notificationclick before our SW at /
    // ever sees them, so clicks never redirect.
    try {
      await this.unregisterOldServiceWorkers();
    } catch (error) {
      // init() is invoked as a floating promise from AppComponent, so a rejection
      // here would surface as an uncaught rejection rather than being handled.
      this.logger.warn('[Push] Could not unregister old service workers:', error);
    }

    try {
      // Explicitly register the SW so it's always present in Safari.
      // Firebase's lazy registration only happens when getToken() succeeds,
      // which means the SW may never be registered in Safari, causing
      // notificationclick to never fire.
      await navigator.serviceWorker.register(this.appVariables.FIREBASE_SERVICE_WORKER_PATH, {
        scope: '/',
      });
    } catch (error) {
      this.logger.error('[Push] ❌ SW registration failed:', error);
    }

    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'NOTIFICATION_CLICKED') {
        this.ngZone.run(() => {
          const locationId = event.data.locationId || this.getUrlParam(event.data.url, 'location');
          if (this.authService.isAuthenticated() && locationId) {
            this.locationSwitcherService.selectLocation(locationId, true);
          }

          const partyId = event.data.partyId || this.getUrlParam(event.data.url, 'partyId');

          if (event.data.url) {
            void this.router.navigateByUrl(this.removeUrlParams(event.data.url, ['location']));
          }

          if (partyId) {
            this.notificationCenterService.markNotificationAsRead(partyId)
              .subscribe();
          }
        });
      }

      if (event.data?.type === 'PLAY_NOTIFICATION_SOUND') {
        const audio = new Audio('/assets/audio/alert_pop.mp3');
        audio.play().catch((err) => {
          this.logger.warn('[Push] Could not play notification sound:', err);
        });
      }
    });
  }

  async unregisterOldServiceWorkers(): Promise<void> {
    if (!('serviceWorker' in navigator)) return;

    const registrations = await navigator.serviceWorker.getRegistrations();

    for (const registration of registrations) {
      if (registration.scope.includes('firebase-cloud-messaging-push-scope')) {
        await registration.unregister();
      }
    }
  }

  private async registerCustomServiceWorker(): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    try {
      const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });

      if (registration.installing) {
        await new Promise<void>((resolve) => {
          registration.installing!.addEventListener('statechange', (e) => {
            if ((e.target as ServiceWorker).state === 'activated') {
              resolve();
            }
          });
        });
      }

      await navigator.serviceWorker.ready;

      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'NAVIGATE') {
          window.location.href = event.data.url;
        }
      });

    } catch (error) {
      this.logger.error('[Push] ❌ Failed to register service worker:', error);
    }
  }

  requestPermission(): void {
    if (Capacitor.isNativePlatform()) {
      return;
    }

    from(this.getFcmToken())
      .pipe(
        filter((token): token is string => Boolean(token)),
        concatMap((token) => {
          let deviceId: string = this.storage.get('firebaseDeviceId');

          if (!deviceId) {
            deviceId = v4();
            this.storage.set('firebaseDeviceId', deviceId);
          }

          return this.fcmTokenApiService.setFcmToken({
            deviceId,
            token,
            type: FcmTokenTypes.web,
          });
        }),
      )
      // Without an error handler a rejected getToken() reaches Angular's
      // ErrorHandler and is reported as an app error, which is wrong for the
      // ordinary case of a browser or a user that will not accept push.
      .subscribe({
        error: (error) => {
          this.logger.warn('[Push] Token registration skipped:', error);
        },
      });
  }

  /**
   * Resolves to an FCM token, or null when this browser/user cannot receive web
   * push. Callers must treat null as "no push here", not as a failure.
   *
   * It can still reject: Firebase prompts when permission is 'default' and
   * rejects if the user dismisses the prompt, and getToken() has its own network
   * and registration failures. requestPermission() handles those.
   */
  async getFcmToken(): Promise<string | null> {
    const messaging = await this.firebaseService.getMessaging();

    if (!messaging) {
      return null;
    }

    // getToken() reads Notification.requestPermission(). Browsers without the
    // Notification global (iOS Safari outside a home-screen PWA) throw a bare
    // ReferenceError, and a user who has blocked notifications makes it reject
    // with messaging/permission-blocked. Both are expected states, not errors.
    if (typeof Notification === 'undefined' || Notification.permission === 'denied') {
      return null;
    }

    const swRegistration = 'serviceWorker' in navigator
      ? await navigator.serviceWorker.ready
      : undefined;

    return getToken(
      messaging,
      {
        vapidKey: this.appVariables.FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: swRegistration,
      },
    );
  }

  private getUrlParam(urlString: string | undefined, key: string): string | null {
    if (!urlString) {
      return null;
    }

    try {
      const url = new URL(urlString, window.location.origin);

      return url.searchParams.get(key);
    } catch (_error) {
      return null;
    }
  }

  private removeUrlParams(urlString: string, paramsToRemove: string[]): string {
    try {
      const url = new URL(urlString, window.location.origin);
      paramsToRemove.forEach(param => url.searchParams.delete(param));

      return `${url.pathname}${url.search}${url.hash}`;
    } catch (_error) {
      return urlString;
    }
  }
}
